/**
 * Subscription System - Core Type Definitions
 *
 * This file contains all core types for the subscription system.
 */

// ============================================================================
// PLAN CODES
// ============================================================================

/**
 * All available plan codes
 * Single source of truth for plan identification
 */
export const PLAN_CODES = {
  FREE: 'free',
  INDIVIDUAL: 'individual',
  TEAM: 'team',
  // Team AI — RETIRED (2026-09). AI is now included in Individual and Team.
  // Kept only so a legacy subscription still on it resolves and displays; the
  // backend hides it (isPublic=false) and rejects checkout / plan changes to it.
  AI: 'ai',
} as const

export type PlanCode = typeof PLAN_CODES[keyof typeof PLAN_CODES]

/**
 * Paid plan codes — "is this subscriber paying?". Includes the retired Team AI
 * plan so a legacy subscriber on it is still treated as paid. For "what can be
 * bought or trialed" use PURCHASABLE_PLAN_CODES.
 */
export const PAID_PLAN_CODES = [
  PLAN_CODES.INDIVIDUAL,
  PLAN_CODES.TEAM,
  PLAN_CODES.AI,
] as const
export type PaidPlanCode = typeof PAID_PLAN_CODES[number]

/** Plan codes that can be bought or trialed today (Team AI is retired). */
export const PURCHASABLE_PLAN_CODES = [
  PLAN_CODES.INDIVIDUAL,
  PLAN_CODES.TEAM,
] as const
export type PurchasablePlanCode = typeof PURCHASABLE_PLAN_CODES[number]

/** True when the given plan code is a paid tier (requires checkout). */
export function isPaidPlanCode(
  code: PlanCode | null | undefined,
): code is PaidPlanCode {
  return (
    code != null && (PAID_PLAN_CODES as ReadonlyArray<string>).includes(code)
  )
}

/**
 * Workspace types (string constants)
 */
export const WORKSPACE_TYPES = {
  PERSONAL: 'personal',
  TEAM: 'team',
} as const

export type WorkspaceType = typeof WORKSPACE_TYPES[keyof typeof WORKSPACE_TYPES]

/**
 * Workspace type enum values (numeric)
 * Must match backend Moly.Domain.WorkspaceType enum
 */
export const WORKSPACE_TYPE_VALUES = {
  PERSONAL: 1,
  TEAM: 2,
} as const

export type WorkspaceTypeValue = typeof WORKSPACE_TYPE_VALUES[keyof typeof WORKSPACE_TYPE_VALUES]

/**
 * Progress types for workspace statuses
 * These are hardcoded types that statuses can be assigned to
 */
export const PROGRESS_TYPES = {
  BACKLOG: 'BL',
  PLANNED: 'PL',
  IN_PROGRESS: 'IP',
  COMPLETED: 'CD',
  CANCELLED: 'CL',
} as const

export type ProgressType = typeof PROGRESS_TYPES[keyof typeof PROGRESS_TYPES]

/**
 * Canonical workflow ordering for progress types.
 * Lower index = earlier in the workflow (Backlog → Cancelled).
 * The Status Management UI sorts/groups by this order.
 */
export const PROGRESS_TYPE_ORDER: ReadonlyArray<ProgressType> = [
  PROGRESS_TYPES.BACKLOG,
  PROGRESS_TYPES.PLANNED,
  PROGRESS_TYPES.IN_PROGRESS,
  PROGRESS_TYPES.COMPLETED,
  PROGRESS_TYPES.CANCELLED,
] as const

const PROGRESS_TYPE_INDEX: Readonly<Record<ProgressType, number>> =
  PROGRESS_TYPE_ORDER.reduce<Record<ProgressType, number>>((acc, type, i) => {
    acc[type] = i
    return acc
  }, {} as Record<ProgressType, number>)

/**
 * Returns the canonical workflow position of a progress type.
 * Unknown values are sorted to the end (defensive against schema drift).
 */
export function getProgressTypeOrder(pt: string | null | undefined): number {
  if (pt == null) return PROGRESS_TYPE_ORDER.length
  return PROGRESS_TYPE_INDEX[pt as ProgressType] ?? PROGRESS_TYPE_ORDER.length
}

// ============================================================================
// SUBSCRIPTION STATUS
// ============================================================================

/**
 * All possible subscription statuses
 */
export const SUBSCRIPTION_STATUS = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
} as const

export type SubscriptionStatus =
  typeof SUBSCRIPTION_STATUS[keyof typeof SUBSCRIPTION_STATUS]

/**
 * Billing interval options
 */
export const BILLING_INTERVALS = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual',
} as const

export type BillingInterval =
  typeof BILLING_INTERVALS[keyof typeof BILLING_INTERVALS]

// ============================================================================
// GLOBAL USAGE LIMITS
// ============================================================================

/**
 * Global usage limits apply across the entire workspace
 * These are separate from feature-specific limits
 */
export const USAGE_LIMITS = {
  WORKSPACES: 'workspaces',
  MEMBERS: 'members',
  TASKS: 'tasks',
  STORAGE: 'storage',
  // Per-file upload cap (bytes). Free = 5 MB; Individual / Team = 50 MB; -1 = unlimited.
  // Distinct from STORAGE which is the per-workspace total quota.
  MAX_FILE_SIZE_BYTES: 'maxFileSizeBytes',
  PROJECTS: 'projects',
  LABELS: 'labels',
  LABEL_GROUPS: 'labelGroups',
  ACTIVITY_HISTORY_DAYS: 'activityHistoryDays',
  GUESTS: 'guests',
  // AI token budgets (per workspace / month). 0 = blocked, >0 = budget, -1 = unlimited.
  // Funded on Individual and Team; 0 on Free.
  AI_CHAT_TOKENS_MONTHLY: 'aiChatTokensMonthly',
  AI_EMBEDDING_TOKENS_MONTHLY: 'aiEmbeddingTokensMonthly',
  // AI trial caps. Replace the monthly budget while the subscription is trialing and
  // count usage over the whole trial (backend AiLimitResolver). Optional per plan.
  AI_TRIAL_CHAT_TOKENS: 'aiTrialChatTokens',
  AI_TRIAL_EMBEDDING_TOKENS: 'aiTrialEmbeddingTokens',
} as const

export type UsageLimitKey = typeof USAGE_LIMITS[keyof typeof USAGE_LIMITS]

// ============================================================================
// PLAN CONFIGURATION TYPES
// ============================================================================

/**
 * Usage limits configuration for a plan
 * Record ensures ALL limits are defined (the AI trial caps are optional)
 * -1 represents unlimited
 */
export type AiTrialLimitKey =
  | typeof USAGE_LIMITS.AI_TRIAL_CHAT_TOKENS
  | typeof USAGE_LIMITS.AI_TRIAL_EMBEDDING_TOKENS

export type PlanLimits = Record<Exclude<UsageLimitKey, AiTrialLimitKey>, number> &
  Partial<Record<AiTrialLimitKey, number>>

/**
 * Current usage metrics for a workspace
 * Partial because not all limits may be tracked yet
 */
export type UsageMetrics = Partial<Record<UsageLimitKey, number>>

// ============================================================================
// DATABASE DOCUMENTS
// ============================================================================

export interface SubscriptionDocument {
  id: string
  envId: string
  planId: string
  status: SubscriptionStatus

  // Trial tracking
  trialStartDate: number | null
  trialEndDate: number | null

  // Billing cycle
  currentPeriodStart: number
  currentPeriodEnd: number
  billingInterval: BillingInterval

  // Cancellation
  canceledAt: number | null
  cancelAtPeriodEnd: boolean

  // Payment provider — dual-provider since 2026-05-07 (RxDB schema v1).
  // `billingProvider` is the routing key; resolve provider ids via:
  //   subscription.billingProvider === 'paddle'
  //     ? subscription.paddleSubscriptionId
  //     : subscription.stripeSubscriptionId
  // (NOT a null-coalesce — both ids being populated would otherwise route
  // a Stripe row to Paddle.)
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  paddleCustomerId: string | null
  paddleSubscriptionId: string | null
  billingProvider: 'stripe' | 'paddle'
  billingEmail: string

  // Audit fields
  isDeleted: boolean
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null
}

export interface PlanDocument {
  id: string
  code: PlanCode
  name: string
  description: string

  // Pricing (in cents)
  priceMonthly: number
  priceAnnual: number
  currency: string

  // Plan configuration
  limits: PlanLimits
  features?: Record<string, unknown>

  // Display
  isPublic: boolean
  sortOrder: number
  highlightText: string | null
  ctaText: string | null

  // Audit fields
  isDeleted: boolean
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null
}

export interface UsageDocument {
  id: string
  envId: string
  usage: UsageMetrics
  lastCalculated: number
  isDeleted: boolean
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null
}

// ============================================================================
// COMPUTED STATE TYPES
// ============================================================================

/**
 * Complete subscription info with computed properties
 * Used in context and state management
 */
export interface SubscriptionInfo {
  subscription: SubscriptionDocument | null
  plan: PlanDocument | null
  usage: UsageDocument | null

  // Computed boolean flags
  isTrialing: boolean
  trialDaysLeft: number
  isExpired: boolean
  isActive: boolean
  isPastDue: boolean
  isCanceled: boolean
  canUpgrade: boolean
  canDowngrade: boolean

  // Current tier
  currentTier: PlanCode | null
  nextTier: PlanCode | null
  previousTier: PlanCode | null
}

// ============================================================================
// USAGE LIMIT CHECK RESULTS
// ============================================================================

/**
 * Result of checking usage limits
 */
export interface UsageLimitCheckResult {
  isOverLimit: boolean
  currentUsage: number
  limit: number
  remaining: number
  percentUsed: number
  isUnlimited: boolean
  canProceed: boolean
  warningThreshold?: number
  isNearingLimit?: boolean
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

export interface StartTrialRequest {
  billingEmail: string
}

export interface UpgradeSubscriptionRequest {
  planId: string
  billingInterval: BillingInterval
}

export interface CancelSubscriptionRequest {
  reason?: string
  feedback?: string
  cancelAtPeriodEnd: boolean
}

export interface SubscriptionResponse {
  subscription: SubscriptionDocument
  plan: PlanDocument
  usage: UsageDocument
  /**
   * Per-plan trial eligibility map keyed by paid plan code, e.g.
   * { individual: true, team: false }. REST-only — not synced via RxDB.
   * UI reads this from SubscriptionContext (which wraps GET /subscriptions/current)
   * to switch CTA between "Start 30-day trial" and "Upgrade now".
   */
  trialEligibility?: Partial<Record<PaidPlanCode, boolean>>
}

/**
 * Response envelope for any billing operation that defers authoritative state
 * to a Stripe webhook (CreateCheckoutSession, ChangePlan, Cancel). Frontend
 * polls /subscriptions/current until the returned subscription matches
 *   planId === expectedPlanId
 *   AND status ∈ expectedStatuses
 *   AND (expectedCancelAtPeriodEnd === undefined
 *        || cancelAtPeriodEnd === expectedCancelAtPeriodEnd).
 */
export interface ProcessingResponse {
  status: 'processing'
  expectedPlanCode: PlanCode
  expectedPlanId: string
  expectedStatuses: Array<SubscriptionStatus>
  expectedCancelAtPeriodEnd?: boolean
  // Set on CreateCheckoutSession when a Stripe Checkout URL is needed (Free →
  // Individual / Team flow). Null on ChangePlan / Cancel.
  checkoutSessionId?: string | null
  checkoutUrl?: string | null
}

/**
 * Codes returned in plan-change-preflight violations. Phase 0 emits the three
 * Individual-related ones; Phase 3 extends with Free-cap and second-Free-workspace.
 */
export const PLAN_CHANGE_BLOCKED_CODES = {
  // Phase 0: switching INTO Individual requires WorkspaceType.Personal +
  // exactly 1 member + 0 guests.
  MEMBERS: 'plan_change_blocked_members',
  WORKSPACE_TYPE: 'plan_change_blocked_workspace_type',
  GUESTS: 'plan_change_blocked_guests',
  // Phase 3: switching INTO Free is blocked when usage exceeds Free's limits
  // (the violation's currentValue is "{key}:current/limit") or the user
  // already owns another Free workspace.
  QUOTA: 'plan_change_blocked_quota',
  WORKSPACES: 'plan_change_blocked_workspaces',
} as const

export type PlanChangeBlockedCode =
  typeof PLAN_CHANGE_BLOCKED_CODES[keyof typeof PLAN_CHANGE_BLOCKED_CODES]

export interface PlanChangePreflightViolation {
  code: PlanChangeBlockedCode | string // 'string' so Phase 3 codes don't break the type
  count?: number
  currentValue?: string
}

export interface PlanChangePreflightResponse {
  blocked: boolean
  violations: Array<PlanChangePreflightViolation>
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isPlanCode(value: unknown): value is PlanCode {
  return (
    typeof value === 'string' &&
    Object.values(PLAN_CODES).includes(value as PlanCode)
  )
}

export function isSubscriptionStatus(
  value: unknown,
): value is SubscriptionStatus {
  return (
    typeof value === 'string' &&
    Object.values(SUBSCRIPTION_STATUS).includes(value as SubscriptionStatus)
  )
}

export function isUsageLimitKey(value: unknown): value is UsageLimitKey {
  return (
    typeof value === 'string' &&
    Object.values(USAGE_LIMITS).includes(value as UsageLimitKey)
  )
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Plan tier hierarchy for comparisons. Lower index = lower tier.
 * Individual sits between Free and Team because it unlocks paid features
 * for one solo user, while Team adds multi-member collaboration.
 */
export type PlanTierLevel = {
  readonly [PLAN_CODES.FREE]: 0
  readonly [PLAN_CODES.INDIVIDUAL]: 1
  readonly [PLAN_CODES.TEAM]: 2
  // Legacy Team AI ranks equal to Team: moving between them is a switch, not
  // an upgrade or downgrade.
  readonly [PLAN_CODES.AI]: 2
}

export type PlanComparison = 'lower' | 'same' | 'higher'

/**
 * Subscription actions
 */
export const SUBSCRIPTION_ACTIONS = {
  START_TRIAL: 'start_trial',
  UPGRADE: 'upgrade',
  DOWNGRADE: 'downgrade',
  CANCEL: 'cancel',
  REACTIVATE: 'reactivate',
  UPDATE_BILLING: 'update_billing',
  CHANGE_INTERVAL: 'change_interval',
} as const

export type SubscriptionAction =
  typeof SUBSCRIPTION_ACTIONS[keyof typeof SUBSCRIPTION_ACTIONS]

/**
 * Subscription event for analytics/audit
 */
export interface SubscriptionEvent {
  action: SubscriptionAction
  fromPlan?: PlanCode
  toPlan?: PlanCode
  timestamp: number
  userId: string
  metadata?: Record<string, unknown>
}
